import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DoutoBanner from './DoutoBanner';
import SafeIcon from '../common/SafeIcon';
import LoginModal from './LoginModal';
import { useLocale } from './LocaleProvider';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [localeChanging, setLocaleChanging] = useState(false);
  const [switchingTo, setSwitchingTo] = useState(null);
  const { locale, setLocale, t } = useLocale();
  const navigate = useNavigate();

  function changeLocale(nextLocale) {
    if (nextLocale === locale || localeChanging) {
      setIsOpen(false);
      return;
    }
    setLocaleChanging(true);
    setSwitchingTo(nextLocale);
    window.eidocAnalytics?.capture('locale_switch_clicked', {
      from_locale: locale,
      to_locale: nextLocale
    });
    setTimeout(() => {
      const currentPath = window.location.pathname;
      const sharedPlatformRoute = /^\/(?:dashboard|gestao|conta|admin|redefinir-senha)(?:\/|$)/.test(currentPath) || /^\/avaliacao\/[^/]+$/.test(currentPath);
      // Keep an authenticated platform flow on its current host: .com.br and
      // .org cannot share cookies. Public pages still switch to the locale domain.
      if (user && sharedPlatformRoute) {
        setLocale(nextLocale);
        setLocaleChanging(false);
        setSwitchingTo(null);
        return;
      }
      if (typeof window !== 'undefined' && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
        const destination = sharedPlatformRoute
          ? `${currentPath}${window.location.search}${window.location.hash}`
          : nextLocale === 'es' ? '/es' : '/';
        window.localStorage.setItem('minimental_locale', nextLocale);
        window.location.assign(`https://minimental.${nextLocale === 'es' ? 'org' : 'com.br'}${destination}`);
      } else {
        setLocale(nextLocale);
        navigate(nextLocale === 'es' ? '/es' : '/');
        setLocaleChanging(false);
        setSwitchingTo(null);
      }
    }, window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : 180);
    setIsOpen(false);
  }

  useEffect(() => {
    fetch('/api/auth/session', { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((session) => {
        const currentUser = session.user || null;
        setUser(currentUser);
        if (currentUser) {
          if (currentUser.id) window.eidocAnalytics?.identify(currentUser.id, { product: 'minimental' });
          window.eidocAnalytics?.capture('authenticated_session_started', { surface: 'site' });
        }
      })
      .catch(() => {});
  }, []);

  async function signOut() {
    setBusy(true);
    try {
      await fetch('/api/auth/sign-out', { method: 'POST', credentials: 'same-origin' });
      setUser(null);
      window.location.assign('/');
    } finally {
      setBusy(false);
    }
  }

  const shownLocale = switchingTo || locale;
  const navLinks = locale === 'es' ? [
    { name: 'Qué es el Mini Mental', href: '/es/mini-examen-del-estado-mental' },
    { name: 'Aplicación', href: '/es/test-mini-mental-online' },
    { name: 'Puntuación', href: '/es/calculadora-mini-mental' },
  ] : [
    { name: t('calculator'), href: '/#calculadora' },
    { name: 'Guias de uso', href: '/guias' },
    { name: 'Sobre o MEEM', href: '/#sobre-o-meem' },
  ];

  return (
    <header className="sticky top-0 z-50 h-16 no-print border-b border-[var(--hairline)] bg-[var(--bg)]">
      <div className="mx-auto flex h-full max-w-[1180px] items-center justify-between gap-5 px-4">
        <Link to="/" className="inline-flex min-h-11 items-center whitespace-nowrap text-lg font-semibold tracking-[-0.03em] text-[var(--primary)] no-underline">
          <svg aria-hidden="true" focusable="false" width="22" height="16" viewBox="0 0 22 16" className="mr-2 shrink-0">
            <rect x="0" y="3" width="4" height="10" rx="1" fill="var(--accent)" />
            <rect x="6" y="3" width="4" height="10" rx="1" fill="var(--accent)" />
            <rect x="12" y="3" width="4" height="10" rx="1" fill="var(--accent)" />
            <rect x="18.5" y="3.5" width="3" height="9" rx="1" fill="none" stroke="var(--cell-border)" strokeDasharray="2 1.5" />
          </svg>
          MiniMental<span className="font-normal text-[var(--text-muted)]">{locale === 'es' ? '.org' : '.com.br'}</span>
        </Link>

        <nav aria-label={locale === 'es' ? 'Principal' : 'Principal'} className="hidden lg:flex items-center gap-5 text-[0.9375rem]">
          {navLinks.map((link) => (
            <Link key={link.name} to={link.href} data-analytics-event={link.name === 'Suporte' ? 'support_clicked' : undefined} className="inline-flex min-h-11 items-center font-medium text-[var(--text-muted)] no-underline hover:text-[var(--primary)] hover:underline">
              {link.name}
            </Link>
          ))}
            {locale !== 'es' && <DoutoBanner placement="menu" variant="menu" className="inline-flex min-h-11 items-center font-medium text-[var(--text-muted)] no-underline hover:text-[var(--primary)] hover:underline" />}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden sm:inline-flex min-h-11 items-center rounded-full border border-[var(--hairline)] bg-white p-1" role="group" aria-label={t('language')} aria-busy={localeChanging}>
            <button type="button" onClick={() => changeLocale('pt')} disabled={localeChanging} aria-pressed={shownLocale === 'pt'} className={`min-h-11 rounded-full px-3 text-sm font-semibold transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] motion-reduce:transition-none ${shownLocale === 'pt' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-muted)] hover:bg-[var(--bg)]'}`}>Português</button>
            <button type="button" onClick={() => changeLocale('es')} disabled={localeChanging} aria-pressed={shownLocale === 'es'} className={`min-h-11 rounded-full px-3 text-sm font-semibold transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] motion-reduce:transition-none ${shownLocale === 'es' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-muted)] hover:bg-[var(--bg)]'}`}>Español</button>
          </div>
          <div className="inline-flex sm:hidden items-center rounded-full border border-[var(--hairline)] bg-white p-0.5" role="group" aria-label={t('language')} aria-busy={localeChanging}>
            <button type="button" onClick={() => changeLocale('pt')} disabled={localeChanging} aria-label="Português — Brasil" aria-pressed={shownLocale === 'pt'} className={`min-h-11 min-w-11 rounded-full px-2 text-xs font-bold ${shownLocale === 'pt' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-muted)]'}`}>PT</button>
            <button type="button" onClick={() => changeLocale('es')} disabled={localeChanging} aria-label="Español" aria-pressed={shownLocale === 'es'} className={`min-h-11 min-w-11 rounded-full px-2 text-xs font-bold ${shownLocale === 'es' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-muted)]'}`}>ES</button>
          </div>
          {user ? (
            <>
              <Link to="/dashboard" className="hidden sm:inline-flex btn-secondary !py-1 text-sm no-underline">{locale === 'es' ? 'Panel' : 'Painel'}</Link>
              <button type="button" onClick={signOut} disabled={busy} className="inline-flex min-h-11 items-center px-2 text-sm font-medium text-[var(--text-muted)] hover:text-[var(--primary)] disabled:opacity-50">{t('logout')}</button>
            </>
          ) : (
            <button type="button" onClick={() => setLoginOpen(true)} className="hidden sm:inline-flex btn-secondary !py-1 text-sm">{t('login')}</button>
          )}
          <button className="lg:hidden inline-flex h-11 w-11 items-center justify-center text-[var(--primary)]" onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} aria-label={locale === 'es' ? 'Menú de navegación' : 'Menu de navegação'}>
            <SafeIcon name={isOpen ? 'X' : 'Menu'} className="w-6 h-6" />
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-[var(--background)] border-b border-[var(--hairline)] px-4 py-2 flex flex-col">
          {navLinks.map((link) => (
            <Link key={link.name} to={link.href} data-analytics-event={link.name === 'Suporte' ? 'support_clicked' : undefined} className="flex min-h-12 items-center border-b border-[var(--border)] text-lg font-medium no-underline text-[var(--primary)]" onClick={() => setIsOpen(false)}>
              {link.name}
            </Link>
          ))}
          {locale !== 'es' && <DoutoBanner placement="menu" variant="menu" className="flex min-h-12 items-center border-b border-[var(--border)] font-semibold text-[var(--accent)] no-underline" />}
          {user ? (
            <>
              <Link to="/dashboard" className="flex min-h-12 items-center border-b border-[var(--border)] text-[var(--primary)] no-underline" onClick={() => setIsOpen(false)}>{locale === 'es' ? 'Panel' : 'Painel'}</Link>
              <button type="button" onClick={signOut} disabled={busy} className="flex min-h-12 items-center text-left font-bold text-[var(--primary)]">{t('logout')}</button>
            </>
          ) : (
            <button type="button" onClick={() => { setIsOpen(false); setLoginOpen(true); }} className="flex min-h-12 items-center text-left font-semibold text-[var(--accent)]">{t('login')}</button>
          )}
        </div>
      )}
      {loginOpen && <LoginModal onClose={() => setLoginOpen(false)} onAuthenticated={async (nextUser) => {
        setUser(nextUser);
        setLoginOpen(false);
        let signals = {};
        try {
          const response = await fetch("/api/auth/login-signal", { method: "POST", credentials: "same-origin" });
          signals = await response.json();
        } catch { /* sem sinal, cai no dashboard normal */ }
        window.location.assign(signals.showDoutoReengagement ? "/dashboard?douto=1" : "/dashboard");
      }} />}
    </header>
  );
}
